/**
 * inlineHtmlStyles.ts — mirrored from corporate-dashboard/lib/inline-html-styles.ts,
 * as alertHeroStyle.ts is, for the account-alert editors (RichTextEditor). Edit
 * both together.
 */

/**
 * Flattens pasted HTML so that its CSS cannot escape the element it is dropped into.
 *
 * A <style> block inserted through innerHTML is parsed and applied by the browser to the
 * WHOLE document — it is not scoped to the node it was inserted under. So a user who pastes
 * a complete HTML page into the WYSIWYG restyles every page that later renders that content:
 * the form builder preview, the compare dialogs, and the eForm renderer in the patient UI.
 *
 * This walks the pasted markup, resolves every stylesheet rule against it, writes the winning
 * declarations onto the matched elements as inline styles, and then drops the stylesheet.
 * Rules with no inline equivalent — @media, @keyframes, @font-face, :hover, ::before and the
 * like — cannot be carried over, so they are removed and counted for the caller to report.
 */

export interface NormalizedHtml {
  /** The rewritten markup: body content only, no stylesheets, no scripts. */
  html: string
  /** Rules that found something to apply to and were merged into style attributes. */
  inlined: number
  /** Rules that were thrown away because nothing inline can express them. */
  dropped: number
  /** True when the markup was rewritten at all. */
  changed: boolean
}

/**
 * Cheap gate. Ordinary editor content — the markup this component produces itself — never
 * contains any of these, and re-parsing it would churn the markup (attribute order, quoting)
 * for no reason, marking clean blocks dirty.
 */
const NEEDS_NORMALIZE = /<\s*(style|link|script|meta|base|title|html|head|body)\b|\son[a-z]+\s*=/i

/**
 * Selectors that describe a state or a generated box rather than an element, so there is no
 * element to hang a style attribute on. :first-child, :nth-child and :not() are deliberately
 * absent — those are static and querySelectorAll resolves them fine.
 */
const NON_INLINABLE =
  /::|:(?:hover|focus(?:-within|-visible)?|active|visited|link|any-link|target|checked|indeterminate|default|disabled|enabled|required|optional|valid|invalid|in-range|out-of-range|read-only|read-write|placeholder-shown|autofill|user-invalid|user-valid|before|after|first-line|first-letter|selection|marker|backdrop)\b/i

const CSS_STYLE_RULE = 1

/**
 * The CSSOM hands back a shorthand as its longhands — `background: yellow` arrives as nine
 * separate declarations — which would bloat every style attribute we write. Where the engine
 * can still serialize the shorthand, it is used and the longhands are folded away. Only the
 * shorthands that actually show up in pasted content are listed; anything else stays longhand,
 * which is verbose but never wrong. `border` is deliberately absent: border-radius shares its
 * prefix without being part of it.
 */
const SHORTHANDS: Array<{ name: string; longhands: string[] }> = [
  {
    name: "background",
    longhands: [
      "background-image", "background-position", "background-position-x", "background-position-y",
      "background-size", "background-repeat", "background-repeat-x", "background-repeat-y",
      "background-origin", "background-clip", "background-attachment", "background-color",
    ],
  },
  { name: "margin", longhands: ["margin-top", "margin-right", "margin-bottom", "margin-left"] },
  { name: "padding", longhands: ["padding-top", "padding-right", "padding-bottom", "padding-left"] },
  {
    name: "border-radius",
    longhands: [
      "border-top-left-radius", "border-top-right-radius",
      "border-bottom-right-radius", "border-bottom-left-radius",
    ],
  },
  { name: "list-style", longhands: ["list-style-type", "list-style-position", "list-style-image"] },
  { name: "outline", longhands: ["outline-color", "outline-style", "outline-width"] },
  {
    name: "text-decoration",
    longhands: ["text-decoration-line", "text-decoration-color", "text-decoration-style", "text-decoration-thickness"],
  },
  { name: "overflow", longhands: ["overflow-x", "overflow-y"] },
  {
    name: "font",
    longhands: ["font-style", "font-variant", "font-variant-caps", "font-weight", "font-stretch", "font-size", "line-height", "font-family"],
  },
]

interface Declaration {
  prop: string
  value: string
  important: boolean
}

interface FlatRule extends Declaration {
  spec: number
  order: number
}

/**
 * Approximate CSS specificity — ids, then classes/attributes/pseudo-classes, then type
 * selectors. Exact conformance does not matter here; it only has to order rules against each
 * other the way the browser would have.
 */
function specificity(selector: string): number {
  const s = selector.replace(/\\./g, "").replace(/\[[^\]]*\]/g, " [] ")
  const ids = (s.match(/#[\w-]+/g) || []).length
  const attrs = (s.match(/\[\]/g) || []).length
  const classes = (s.match(/\.[\w-]+/g) || []).length
  const pseudos = (s.match(/:[\w-]+/g) || []).length
  const types = (s.replace(/[#.:][\w-]+/g, " ").match(/(?:^|[\s>+~(,])[a-zA-Z][\w-]*/g) || []).length
  return ids * 10000 + (classes + attrs + pseudos) * 100 + types
}

/** Style rules nested inside an @media / @supports block, for an honest dropped count. */
function countNested(rule: CSSRule): number {
  const nested = (rule as CSSGroupingRule).cssRules
  if (!nested || nested.length === 0) return 1
  let total = 0
  for (let i = 0; i < nested.length; i++) total += countNested(nested[i])
  return total
}

/** Reads a declaration block back out, collapsing longhands into their shorthand where it can. */
function readDeclarations(style: CSSStyleDeclaration): Declaration[] {
  const props = new Set<string>()
  for (let i = 0; i < style.length; i++) props.add(style[i])

  for (const shorthand of SHORTHANDS) {
    if (!shorthand.longhands.some((l) => props.has(l))) continue
    const value = style.getPropertyValue(shorthand.name)
    // An empty value means the engine could not serialize the shorthand, i.e. only some of its
    // longhands were declared — those have to stay as they are.
    if (!value) continue
    shorthand.longhands.forEach((l) => props.delete(l))
    props.add(shorthand.name)
  }

  // Safety net for an engine that reports both `margin` and `margin-top`: the shorthand covers it.
  // border-radius is the one prefix match that is not a longhand of its apparent shorthand.
  const kept = Array.from(props)
  const redundant = new Set(
    kept.filter((p) =>
      kept.some((s) => s !== p && p.startsWith(`${s}-`) && !(s === "border" && p.endsWith("-radius"))),
    ),
  )

  return kept
    .filter((prop) => !redundant.has(prop))
    .map((prop) => ({
      prop,
      value: style.getPropertyValue(prop),
      important: style.getPropertyPriority(prop) === "important",
    }))
    .filter((d) => d.value !== "")
}

interface ParsedSheet {
  rules: Array<{ selector: string; decls: Declaration[]; spec: number; order: number }>
  dropped: number
}

/**
 * Reads the pasted CSS back through the browser's own parser. The carrier element is attached
 * with media="not all" so the host page can never match a single one of these rules while we
 * hold it, and it is removed as soon as the rules have been copied out.
 */
function parseSheet(cssText: string): ParsedSheet {
  const rules: ParsedSheet["rules"] = []
  // @import would reach out to the network, and we have no way to inline what comes back.
  let dropped = (cssText.match(/@import\b/gi) || []).length
  const carrier = document.createElement("style")
  carrier.media = "not all"
  carrier.textContent = cssText.replace(/@(?:import|charset)[^;]*;/gi, "")
  document.head.appendChild(carrier)
  try {
    const list = carrier.sheet?.cssRules
    let order = 0
    for (let i = 0; list && i < list.length; i++) {
      const rule = list[i]
      if (rule.type !== CSS_STYLE_RULE) {
        dropped += countNested(rule)
        continue
      }
      const styleRule = rule as CSSStyleRule
      const decls = readDeclarations(styleRule.style)
      for (const part of styleRule.selectorText.split(",")) {
        const selector = part.trim()
        if (!selector) continue
        if (NON_INLINABLE.test(selector)) {
          dropped++
          continue
        }
        rules.push({ selector, decls, spec: specificity(selector), order: order++ })
      }
    }
  } catch {
    // Malformed css the parser refused — nothing to inline, and the stylesheet still goes.
  }
  carrier.remove()
  return { rules, dropped }
}

/** What the element itself already says about a property, shorthands expanded by the engine. */
function readOwnValue(el: Element, prop: string): string {
  const style = (el as HTMLElement).style
  if (!style) return ""
  try {
    return style.getPropertyValue(prop)
  } catch {
    return ""
  }
}

/** A declaration beats the one already held on !important first, then specificity, then order. */
function beats(candidate: FlatRule, held: FlatRule): boolean {
  if (candidate.important !== held.important) return candidate.important
  if (candidate.spec !== held.spec) return candidate.spec > held.spec
  return candidate.order >= held.order
}

function stripComments(root: Element): void {
  const walker = root.ownerDocument.createTreeWalker(root, 128 /* SHOW_COMMENT */)
  const comments: Node[] = []
  while (walker.nextNode()) comments.push(walker.currentNode)
  comments.forEach((c) => c.parentNode?.removeChild(c))
}

export function normalizeHtmlStyles(html: string): NormalizedHtml {
  if (!html || typeof document === "undefined" || !NEEDS_NORMALIZE.test(html)) {
    return { html, inlined: 0, dropped: 0, changed: false }
  }

  const doc = new DOMParser().parseFromString(html, "text/html")
  const root = doc.body

  let cssText = ""
  doc.querySelectorAll("style").forEach((el) => {
    cssText += "\n" + (el.textContent || "")
  })
  const externalSheets = Array.from(doc.querySelectorAll("link")).filter((el) =>
    (el.getAttribute("rel") || "").toLowerCase().split(/\s+/).includes("stylesheet"),
  ).length
  const hadStylesheets = cssText.trim().length > 0 || externalSheets > 0

  // An external stylesheet cannot be fetched and inlined, so it is simply gone.
  let dropped = externalSheets
  let inlined = 0

  if (cssText.trim()) {
    const parsed = parseSheet(cssText)
    dropped += parsed.dropped

    const winners = new Map<Element, Map<string, FlatRule>>()
    for (const rule of parsed.rules) {
      let matches: Element[]
      try {
        matches = Array.from(root.querySelectorAll(rule.selector))
      } catch {
        dropped++
        continue
      }
      // Nothing in the pasted fragment answers to this selector — html, body and :root rules
      // land here, since those wrappers are unwrapped away.
      if (matches.length === 0) {
        dropped++
        continue
      }
      inlined++
      for (const el of matches) {
        let held = winners.get(el)
        if (!held) {
          held = new Map<string, FlatRule>()
          winners.set(el, held)
        }
        for (const decl of rule.decls) {
          const candidate: FlatRule = { ...decl, spec: rule.spec, order: rule.order }
          const current = held.get(decl.prop)
          if (!current || beats(candidate, current)) held.set(decl.prop, candidate)
        }
      }
    }

    winners.forEach((decls, el) => {
      const normal: string[] = []
      const important: string[] = []
      Array.from(decls.values())
        .sort((a, b) => a.spec - b.spec || a.order - b.order)
        .forEach((d) => {
          // A property the element already declares on itself would beat this anyway — writing
          // it out as well only leaves a dead declaration behind.
          if (!d.important && el.getAttribute("style") && readOwnValue(el, d.prop)) return
          const text = `${d.prop}: ${d.value}${d.important ? " !important" : ""}`
          if (d.important) important.push(text)
          else normal.push(text)
        })
      // Inside a style attribute the later declaration wins, so the element's own inline styles
      // sit after the stylesheet's normal ones and keep beating them, and the stylesheet's
      // !important ones go last where they beat everything.
      const own = (el.getAttribute("style") || "").trim().replace(/;\s*$/, "")
      const merged = [normal.join("; "), own, important.join("; ")].filter(Boolean).join("; ")
      el.setAttribute("style", merged)
    })
  }

  doc.querySelectorAll("style, link, script, meta, base, title").forEach((el) => el.remove())

  if (hadStylesheets) {
    // With the stylesheet gone these can no longer refer to anything we kept, and leaving them
    // in lets the HOST page's css claim the pasted markup — the same leak in reverse, with a
    // stray class="hidden" quietly deleting the content under tailwind.
    root.querySelectorAll("[class], [id]").forEach((el) => {
      el.removeAttribute("class")
      el.removeAttribute("id")
    })
  }

  root.querySelectorAll("*").forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (/^on/i.test(attr.name)) el.removeAttribute(attr.name)
      else if (/^(href|src|xlink:href)$/i.test(attr.name) && /^\s*javascript:/i.test(attr.value)) {
        el.removeAttribute(attr.name)
      }
    })
  })

  stripComments(root)

  const out = root.innerHTML
  return { html: out, inlined, dropped, changed: out !== html }
}
